import { Container } from '@mui/material';
import { Formik, FormikProps } from 'formik';
import { DateTime } from 'luxon';
import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { SystemMinimumDate, createPivotDateTimeFromDate } from 'src/common';
import { ReportFileNameType, ScheduledEventInput } from 'src/features/reporting/store/types';
import { SCHEDULE_DAYS_OF_WEEK } from '../../constants';
import { Props } from '../container';
import { ReportingSchedulerStepper } from './ReportingSchedulerStepper';
import { SchedulerValidationSchema } from './ReportingSchedulerStepperValidation';

export const CreateScheduledReportComponent = (props: Props) => {
  const history = useHistory();

  useEffect(() => {
    props.resetEditingSchedulerEvent();
    props.setInceptionDate(DateTime.fromISO(SystemMinimumDate).toISODate());
    props.resetReports();
  }, []);

  const handleSubmit = (values: ScheduledEventInput, trigger: boolean) => {
    // Normalise the dates into pivot dates in case they somehow end up storing a local date.
    values.startOn = createPivotDateTimeFromDate(values.startOn).toJSDate();
    if (values.endOn) {
      values.endOn = createPivotDateTimeFromDate(values.endOn).toJSDate();
    }
    props
      .createScheduledEvent(values)
      .unwrap()
      .then((res) => {
        if ((trigger || (!values.recurrenceRule && DateTime.now().hasSame(DateTime.fromJSDate(values.startOn), 'day'))) && res.id) {
          props.triggerScheduledEvent({ id: res.id }).then(() => {
            history.replace('/reporting?tab=scheduler');
          });
        } else {
          history.replace('/reporting?tab=scheduler');
        }
      });
  };

  return (
    <Container maxWidth={false} sx={{ padding: 6 }}>
      <Formik<ScheduledEventInput>
        initialValues={{
          name: '',
          description: '',
          recurrenceType: 'Daily',
          occurrences: 0,
          displayOnInvestorPortal: false,
          startOn: createPivotDateTimeFromDate(new Date()).toJSDate(),
          reportFileNameTemplate: ReportFileNameType.NonMerged.displayName,
          clientSearchFilter: {
            afslIds: [],
            clientIds: [],
            adviserIds: [],
            practiceIds: [],
            serviceType: '',
            clientTypes: [],
            groupIds: [],
            statuses: [],
          },
          recurrenceRule: {
            interval: 1,
            daysOfWeek: SCHEDULE_DAYS_OF_WEEK,
            dayOfMonth: null,
            weekOfMonth: null,
            monthOfYear: null,
          },
          emailParameters: undefined,
          reportParameters: undefined,
          endOn: createPivotDateTimeFromDate(DateTime.now().plus({ weeks: 1 }).toJSDate()).toJSDate(),
          allowTerminatedClients: false,
        }}
        validationSchema={SchedulerValidationSchema}
        validateOnChange={false}
        validateOnMount={false}
        validateOnBlur={false}
        validate={async (values) => {
          try {
            await SchedulerValidationSchema.validate(values, {
              abortEarly: false,
              context: { recurrenceType: values.recurrenceType },
            });
          } catch (e) {}
        }}
        onSubmit={(values: ScheduledEventInput) => handleSubmit(values, false)}
      >
        {(formikProps: FormikProps<ScheduledEventInput>) => (
          <ReportingSchedulerStepper editing={false} {...props} handleSubmit={handleSubmit} formikProps={formikProps} initialStep={0} />
        )}
      </Formik>
    </Container>
  );
};
